import { MAX_ATTACHMENT_BYTES } from '@chat-emp/shared';

/**
 * What the composer will accept before it spends an upload, and what each
 * accepted file turns out to be, in one table.
 *
 * The `accept` attribute on a file input is a filter the dialog applies, not a
 * guarantee: a drag-drop or a "show all files" dialog gets straight past it. So
 * the same list is enforced here, where a rejection costs nothing, rather than
 * discovering it from an upload that already went over the wire.
 *
 * One table because these were three lists over the same five types, agreeing
 * only by hand. Adding webp would mean remembering the MIME set, the `accept`
 * string and the kind, and missing the last would be silent: the upload
 * succeeds and renders as a generic document row, because a kind this does not
 * recognise is a `doc` by design.
 *
 * The types mirror `@ajt/stream-chat`'s `acceptedFiles` on its `Channel`, so an
 * employer can send from either product and have the same files allowed.
 */
export type AttachmentKind = 'image' | 'pdf' | 'doc';

const ACCEPTED: Record<string, { mime: string; kind: AttachmentKind }> = {
  jpg: { mime: 'image/jpeg', kind: 'image' },
  jpeg: { mime: 'image/jpeg', kind: 'image' },
  png: { mime: 'image/png', kind: 'image' },
  pdf: { mime: 'application/pdf', kind: 'pdf' },
  doc: { mime: 'application/msword', kind: 'doc' },
  docx: {
    mime: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    kind: 'doc',
  },
};

export const ACCEPTED_MIME_TYPES: readonly string[] = [
  ...new Set(Object.values(ACCEPTED).map((entry) => entry.mime)),
];

/** Matching extension list for the input's `accept` hint. */
export const ACCEPTED_EXTENSIONS = Object.keys(ACCEPTED)
  .map((extension) => `.${extension}`)
  .join(',');

export { MAX_ATTACHMENT_BYTES };

export interface AttachmentRejection {
  reason: 'type' | 'size';
  message: string;
}

/** `null` when the file may be uploaded. */
export function rejectAttachment(file: File): AttachmentRejection | null {
  if (!ACCEPTED_MIME_TYPES.includes(file.type)) {
    return { reason: 'type', message: 'Only images, PDFs and Word documents can be attached.' };
  }
  if (file.size > MAX_ATTACHMENT_BYTES) {
    const mb = Math.floor(MAX_ATTACHMENT_BYTES / (1024 * 1024));
    return { reason: 'size', message: `Files must be under ${mb}MB.` };
  }
  return null;
}

/** The backend refuses a name whose extension does not match the type, so give it one that does. */
export function uploadName(file: File): string {
  const dot = file.name.lastIndexOf('.');
  const ext = dot === -1 ? '' : file.name.slice(dot + 1).toLowerCase();
  if (ACCEPTED[ext]?.mime === file.type) return file.name;
  const fitting = Object.keys(ACCEPTED).find((e) => ACCEPTED[e]!.mime === file.type);
  return fitting ? `${file.name}.${fitting}` : file.name;
}

/**
 * Which of the three kinds a file name is.
 *
 * Read off the extension rather than the MIME type, because this has to answer
 * for a CDN link as well as for a freshly picked `File`, and a name is all
 * either reliably carries. The wire is no help: Stream reports `image` or
 * nothing, which is why a PDF and a Word document arrive indistinguishable.
 *
 * Anything unrecognised is a `doc`, the kind that promises least. A file this
 * does not know is one nothing should offer to preview.
 */
export function attachmentKind(fileName: string): AttachmentKind {
  const extension = fileName.slice(fileName.lastIndexOf('.') + 1).toLowerCase();
  return ACCEPTED[extension]?.kind ?? 'doc';
}

/**
 * Whether a browser can be trusted to render this in a tab of its own.
 *
 * Images and PDFs, and nothing else: every browser this ships to has a native
 * viewer for both, and none can open a .docx. Deliberately a question about the
 * browser rather than about this app — nothing here renders an attachment
 * itself, it points a tab at a url on another origin.
 */
export function isPreviewable(kind: AttachmentKind): boolean {
  return kind === 'image' || kind === 'pdf';
}

/** `null` when the size is unknown, so a caller can leave the slot empty. */
export function formatFileSize(bytes: number | undefined): string | null {
  if (bytes === undefined || bytes < 0) return null;
  if (bytes < 1024) return `${bytes} B`;
  const kb = bytes / 1024;
  if (kb < 1024) return `${Math.round(kb)} KB`;
  return `${(kb / 1024).toFixed(1)} MB`;
}
